import {
  CASE_TYPE_OPTIONS,
  COMMUNICATION_CATEGORY_OPTIONS,
  capabilityOverTime,
  caseTypeCounts,
  communicationCategoryCounts,
  cumulativeCount,
  dailyActivity,
  edtActivityCounts,
  edtStats,
  formatDuration,
  headlineStats,
  hilloCoverage,
  isCommunication,
  labelFor,
  notDoneRecently,
  procedureCounts,
  procedureLabel,
  selectEntries,
  taggedEntryCount,
  temporalDistribution,
} from '@iculogbook/shared';
import type { AnyEntry, EdtEntry, Entry } from '@iculogbook/shared';
import { useLiveQuery } from 'dexie-react-hooks';
import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router';

import { useAuth } from '../auth/AuthProvider.tsx';
import {
  Button,
  Card,
  EmptyState,
  SegmentedControl,
  Spinner,
  StatTile,
} from '../components/index.ts';
import { casesRepository } from '../db/repositories/cases.ts';
import { communicationsRepository } from '../db/repositories/communications.ts';
import { edtRepository } from '../db/repositories/edt.ts';
import { proceduresRepository } from '../db/repositories/procedures.ts';
import {
  ActivityHeatmap,
  CapabilityProgression,
  CumulativeChart,
  Doughnut,
  HilloBars,
  TemporalGrid,
} from '../features/dashboard/charts.tsx';
import { topWithOther } from '../features/dashboard/chartTheme.ts';
import { BackupReminder } from '../features/data/BackupReminder.tsx';
import {
  RANGE_KEYS,
  RANGE_LABELS,
  last30Days,
  lastWeeks,
  rangeFor,
} from '../features/dashboard/range.ts';
import type { RangeKey } from '../features/dashboard/range.ts';

const DATE = new Intl.DateTimeFormat('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
const MONTH_YEAR = new Intl.DateTimeFormat('en-GB', { month: 'long', year: 'numeric' });

/**
 * The Dashboard, in the order the PRD sets out: what a trainee wants first,
 * first.
 *
 * Every number here comes from the aggregation functions in the shared package
 * — none is recomputed in this file. That is what stops the screen and the PDF
 * disagreeing, which is hazard H4 in COMPLIANCE.md.
 */
export function DashboardRoute() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const userId = user?.id ?? null;

  const [rangeKey, setRangeKey] = useState<RangeKey>('12m');
  // Fixed at mount rather than read during render: a component that re-reads the
  // clock while rendering is impure, and the range would drift under the user.
  const [now] = useState(() => new Date());

  const data = useLiveQuery(async () => {
    if (userId === null) return { entries: [] as Entry[], edtEntries: [] as EdtEntry[] };

    const [cases, procedures, communications, edtEntries] = await Promise.all([
      casesRepository.list(userId),
      proceduresRepository.list(userId),
      communicationsRepository.list(userId),
      edtRepository.list(userId),
    ]);

    return {
      entries: [...cases, ...procedures, ...communications] as Entry[],
      edtEntries: edtEntries as EdtEntry[],
    };
  }, [userId]);

  const entries = data?.entries;
  const edtEntries = data?.edtEntries;

  const range = useMemo(
    () => rangeFor(rangeKey, now, [...(entries ?? []), ...(edtEntries ?? [])]),
    [rangeKey, now, entries, edtEntries],
  );

  /*
   * Filtered once, then handed to every aggregation. Each of them filters
   * defensively again, which is cheap on an already-filtered list — what is not
   * cheap is a dozen separate date comparisons across a seven-year logbook
   * every time the period selector is tapped.
   */
  const inRange = useMemo(
    () => (entries === undefined ? [] : selectEntries(entries, range)),
    [entries, range],
  );

  const edtInRange = useMemo(
    () => (edtEntries === undefined ? [] : selectEntries(edtEntries, range)),
    [edtEntries, range],
  );

  const stats = useMemo(() => headlineStats(inRange, range), [inRange, range]);
  const edt = useMemo(() => edtStats(edtInRange, range), [edtInRange, range]);

  const edtSlices = useMemo(
    () =>
      topWithOther(
        edtActivityCounts(edtInRange, range).map((row) => ({ label: row.label, count: row.count })),
      ),
    [edtInRange, range],
  );

  const recent30 = useMemo(
    () => (entries === undefined ? 0 : headlineStats(entries, last30Days(now)).total),
    [entries, now],
  );

  /*
   * Fixed at twenty trailing weeks regardless of the period selector, the
   * same reasoning as `recent30` above — "have I been consistent lately" is a
   * question about real calendar weeks, not whatever range is picked for the
   * figures elsewhere on the page.
   */
  const heatmapDays = useMemo(
    () =>
      dailyActivity(
        [...(entries ?? []), ...(edtEntries ?? [])] as AnyEntry[],
        lastWeeks(now),
      ),
    [entries, edtEntries, now],
  );

  const capability = useMemo(() => capabilityOverTime(inRange, range), [inRange, range]);

  const procedureSlices = useMemo(
    () =>
      topWithOther(
        procedureCounts(inRange, range).map((row) => ({ label: row.label, count: row.total })),
      ),
    [inRange, range],
  );

  const caseSlices = useMemo(
    () =>
      topWithOther(
        caseTypeCounts(inRange, range).map((row) => ({
          label: labelFor(CASE_TYPE_OPTIONS, row.key),
          count: row.count,
        })),
      ),
    [inRange, range],
  );

  const communicationSlices = useMemo(
    () =>
      topWithOther(
        communicationCategoryCounts(inRange, range).map((row) => ({
          label: row.label,
          count: row.count,
        })),
      ),
    [inRange, range],
  );

  const cumulative = useMemo(() => cumulativeCount(inRange, range), [inRange, range]);

  const temporal = useMemo(() => temporalDistribution(inRange, range), [inRange, range]);

  /*
   * Both take EDT entries as well as clinical ones, because the PDF's
   * curriculum section does. A screen and a document computing the same figure
   * over different populations is hazard H4 in COMPLIANCE.md — and HiLLOs 1 to
   * 4 are exactly the rows EDT contributes.
   */
  const coverage = useMemo(
    () => hilloCoverage([...inRange, ...edtInRange], range),
    [inRange, edtInRange, range],
  );

  const tagged = useMemo(
    () => taggedEntryCount([...inRange, ...edtInRange], range),
    [inRange, edtInRange, range],
  );

  /*
   * Deliberately over the whole logbook rather than the selected period.
   * "Done before but not lately" is an all-time question — asking it of the last
   * 30 days would answer "nothing", every time, which is worse than useless.
   */
  const quiet = useMemo(
    () =>
      entries === undefined ? [] : notDoneRecently(entries, rangeFor('all', now, entries), now),
    [entries, now],
  );

  const recent = useMemo(
    () => [...inRange].sort((a, b) => b.occurredAt.getTime() - a.occurredAt.getTime()).slice(0, 5),
    [inRange],
  );

  if (entries === undefined || edtEntries === undefined) {
    return <Spinner label="Working out your figures" />;
  }

  if (entries.length === 0 && edtEntries.length === 0) {
    return (
      <EmptyState
        title="Nothing to show yet"
        description="Log a few procedures and cases and this becomes a picture of your training — what you do most, how your capability level is moving, and how much of it is out of hours."
        action={
          <Button variant="primary" onClick={() => void navigate('/log')}>
            Log your first entry
          </Button>
        }
      />
    );
  }

  return (
    <div className="flex flex-col gap-5">
      <BackupReminder />

      <SegmentedControl
        label="Period"
        // Word labels, and the same control as the report screen's period
        // picker — the two should read alike.
        compact
        segments={RANGE_KEYS.map((key) => ({ value: key, label: RANGE_LABELS[key] }))}
        value={rangeKey}
        onChange={(next) => setRangeKey(next)}
      />

      <p className="-mt-3 px-1 text-xs text-faint">
        {DATE.format(range.from)} to {DATE.format(range.to)} · {String(stats.days)} days
      </p>

      <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
        <StatTile label="Entries" value={stats.total} />
        <StatTile label="Procedures" value={stats.procedures} />
        <StatTile label="Cases" value={stats.cases} />
        {/* Deliberately fixed: a "recent activity" figure that moved with the
            selector would say nothing the other tiles do not. */}
        <StatTile label="Last 30 days" value={recent30} hint="always 30 days" />
      </div>

      <Card title="Consistency">
        <ActivityHeatmap days={heatmapDays} />
        <p className="pt-2 text-xs text-faint">
          The last 20 weeks, always — not the period above. Just density, no target and no streak
          to keep: a quiet week is often a week of leave or nights, not a gap in anything.
        </p>
      </Card>

      <Card title="What you have been doing">
        <div className="grid gap-6 sm:grid-cols-2">
          <Doughnut title="Procedures" slices={procedureSlices} total={stats.procedures} />
          <Doughnut title="Cases" slices={caseSlices} total={stats.cases} />
          {/* Hidden entirely until there is one — the same rule Procedures and
              Cases don't need, because every trainee has both, but not every
              trainee has logged a Communication yet. */}
          {stats.communications === 0 ? null : (
            <Doughnut
              title="Communication"
              slices={communicationSlices}
              total={stats.communications}
            />
          )}
        </div>
      </Card>

      {/*
        The chart that tells the training story — see the PRD — placed after
        the counts and the two glance-able cards above it rather than
        competing with them for the first thing a trainee sees.
      */}
      <Card title="Capability level over time">
        <CapabilityProgression buckets={capability} />
        <p className="pt-2 text-xs text-faint">
          Entries by FICM capability level, per quarter. Pale is level 1, strongest is level 4.
        </p>
      </Card>

      {/* Hidden entirely until there is one, like the curriculum chart. */}
      {edt.total === 0 ? null : (
        <Card title="Educational Development Time">
          <Doughnut title="Activities" slices={edtSlices} total={edt.total} />
          <p className="pt-2 text-xs text-faint">
            {edt.withDuration === 0
              ? 'No time recorded against these entries yet — it is optional on each one.'
              : `${formatDuration(edt.minutes)} recorded across ${String(edt.withDuration)} of ${String(edt.total)} entries.`}
            {edt.teachingDelivered === 0
              ? ''
              : ` ${String(edt.teachingDelivered)} ${edt.teachingDelivered === 1 ? 'session' : 'sessions'} of teaching delivered.`}
          </p>
        </Card>
      )}

      <Card title="Entries over time">
        <CumulativeChart points={cumulative} />
      </Card>

      <Card title="When you work">
        <TemporalGrid distribution={temporal} />
        <p className="pt-3 text-xs text-faint">
          {String(stats.outOfHoursPercent)}% of entries were nights or weekends. Day is 08:00 to
          19:59; the classification follows the time recorded, not the shift.
        </p>
      </Card>

      {/* Hidden entirely until something is tagged — CURRICULUM.md. */}
      {tagged === 0 ? null : (
        <Card title="Curriculum coverage">
          <HilloBars coverage={coverage} />
          <p className="pt-3 text-xs text-faint">
            {String(tagged)} of {String(stats.total)} entries carry a HiLLO tag. This counts what
            you tagged; it is not an assessment of coverage.
          </p>
        </Card>
      )}

      {quiet.length === 0 ? null : (
        <Card title="Not logged recently">
          <ul className="flex flex-col gap-2">
            {quiet.slice(0, 8).map((row) => (
              <li
                key={row.procedureKey}
                className="flex items-center justify-between gap-3 text-sm"
              >
                <span className="min-w-0 truncate text-text">{row.label}</span>
                <span className="shrink-0 text-xs text-faint">
                  {String(row.totalEver)} logged · last {MONTH_YEAR.format(row.lastDone)}
                </span>
              </li>
            ))}
          </ul>
          <p className="pt-3 text-xs text-faint">
            Procedures you have recorded before but not in the last six months. For information —
            there is no target here, and no suggestion you should have done them.
          </p>
        </Card>
      )}

      <Card title="Most recent">
        <ul className="flex flex-col">
          {recent.map((entry) => (
            <li key={entry.id}>
              <button
                type="button"
                onClick={() =>
                  void navigate(
                    'caseType' in entry
                      ? `/log/case/${entry.id}`
                      : isCommunication(entry)
                        ? `/log/communication/${entry.id}`
                        : `/log/procedure/${entry.id}`,
                  )
                }
                className="flex min-h-touch w-full items-center justify-between gap-3 rounded-md px-2 text-left hover:bg-surface-raised"
              >
                <span className="min-w-0 truncate text-sm text-text">
                  {'caseType' in entry
                    ? entry.title
                    : isCommunication(entry)
                      ? labelFor(COMMUNICATION_CATEGORY_OPTIONS, entry.category)
                      : procedureLabel(entry.procedureKey)}
                </span>
                <span className="shrink-0 text-xs text-faint">{DATE.format(entry.occurredAt)}</span>
              </button>
            </li>
          ))}
        </ul>
      </Card>
    </div>
  );
}
